import React, { useState } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';

import { FieldTextInput, Form } from '../../../../components';

import Field from '../../Field';

import submitArrow from '../../../../assets/newsletter/icon-submit-arrow.svg';
import css from './SectionContainer.module.css';

const NEWSLETTER_SECTION_ID = 'newsletter-section';

/**
 * Email subscribe field for the landing-page newsletter section (Figma).
 * Renders an underlined input + submit arrow; Console supplies title and description.
 *
 * @returns {JSX.Element}
 */
const NewsletterSubscribeForm = () => {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);

  const emailLabel = intl.formatMessage({ id: 'NewsletterSubscribeForm.emailLabel' });
  const emailPlaceholder = intl.formatMessage({ id: 'NewsletterSubscribeForm.placeholder' });
  const emailRequired = validators.required(
    intl.formatMessage({ id: 'NewsletterSubscribeForm.emailRequired' })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'NewsletterSubscribeForm.emailInvalid' })
  );

  if (submitted) {
    return (
      <p className={css.newsletterSuccess}>
        <FormattedMessage id="NewsletterSubscribeForm.success" />
      </p>
    );
  }

  return (
    <FinalForm
      onSubmit={() => {
        setSubmitted(true);
      }}
      render={({ handleSubmit, submitting, errors, touched }) => {
        const showError = !!(touched?.email && errors?.email);
        return (
          <Form className={css.newsletterForm} onSubmit={handleSubmit}>
            <div className={css.newsletterRow}>
              <FieldTextInput
                className={css.newsletterField}
                inputRootClass={css.newsletterInput}
                labelClassName={css.newsletterLabel}
                type="email"
                id={`${NEWSLETTER_SECTION_ID}-email`}
                name="email"
                autoComplete="email"
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
                hideErrorMessage
              />
              <button
                className={css.newsletterSubmit}
                type="submit"
                disabled={submitting}
                aria-label={intl.formatMessage({ id: 'NewsletterSubscribeForm.submit' })}
              >
                <img
                  className={css.newsletterSubmitIcon}
                  src={submitArrow}
                  alt=""
                  width="24"
                  height="24"
                />
              </button>
            </div>
            {showError ? (
              <p className={css.newsletterError} role="alert">
                {errors.email}
              </p>
            ) : null}
          </Form>
        );
      }}
    />
  );
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const { className, rootClassName, id, as, children, appearance, options, ...otherProps } = props;
  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);
  const isNewsletterSection = id === NEWSLETTER_SECTION_ID;

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ alt: `Background image for ${id}`, ...appearance }}
          className={className}
          options={options}
        />
      ) : null}

      <div className={css.sectionContent}>
        {children}
        {isNewsletterSection ? <NewsletterSubscribeForm /> : null}
      </div>
    </Tag>
  );
};

export default SectionContainer;
